import { flexRender } from '@tanstack/react-table';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useERSContext, useMapContext } from '@/context';
import { cn } from '@/lib/utils';
import { EditIncident, IncidentDetails } from '.';

export const IncidentCard = ({ cardData, responders, onRowClick }) => {
  const [showCreateIncident, setShowCreateIncident] = useState(false);
  const { selectedIncident } = useMapContext();
  const { chatList, handleChatSelect } = useERSContext();
  const [isSelected, setIsSelected] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!selectedIncident) {
      setIsSelected(false);
      return;
    }
    if (selectedIncident.id === cardData.original?.id) {
      if (!cardData.getIsExpanded()) {
        cardData.toggleExpanded();
      }
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [selectedIncident]);

  return (
    <div
      ref={cardRef}
      key={cardData.id}
      className={cn('rounded-lg hover:bg-subtle', isSelected ? ' border border-interactive-active' : 'border')}
    >
      {showCreateIncident && (
        <EditIncident
          handleFormCancel={() => {
            setShowCreateIncident(false);
          }}
          incidentData={cardData}
          responders={responders}
        />
      )}
      <AnimatePresence initial={false}>
        {cardData.getIsExpanded() ? (
          <motion.div
            key="expanded"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            onAnimationComplete={() => {
              // After expand animation completes, scroll smoothly
              if (selectedIncident?.id === cardData.original?.id && cardRef.current) {
                cardRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              }
            }}
          >
            <IncidentDetails
              data={cardData}
              setShowCreateIncident={setShowCreateIncident}
              onRowClick={onRowClick}
              cardData={cardData?.original}
            />
          </motion.div>
        ) : (
          // Normal collapsed row card
          <motion.div
            key="collapsed"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden cursor-pointer"
            onClick={cardData.getToggleExpandedHandler()}
          >
            <div
              onClick={() => {
                cardData?.getToggleExpandedHandler();
              }}
              className="cursor-pointer grid grid-cols-[repeat(5,1fr)_auto] gap-2 px-2 py-3 text-xs font-normal"
            >
              {cardData
                .getVisibleCells()
                .filter(cell => !cell.column.columnDef.meta?.hidden)
                .map(cell => (
                  <span key={cell.id} className="flex flex-row items-center">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </span>
                ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
